import { tv } from 'tailwind-variants';

export const desktopHeaderVariants = tv(
  {
    slots: {
      base: ['relative', 'flex flex-col', 'w-full items-center justify-center'],
      topBar: [
        'w-full bg-white',
        'border-b-3 border-b-primary md:border-0',
        'relative z-140 transform-gpu',
      ],
      topBarContainer: [
        'flex justify-between items-center flex-wrap',
        'w-full max-w-292.5 mx-auto md:ps-3.75',
        // Tablet-only vertical breathing room; reset at lg so desktop height stays as-is
        'md:py-2 lg:py-0',
      ],
      navBar: ['relative w-full bg-nav-secondary hidden lg:block'],
      navBarContainer: ['w-full max-w-292.5 mx-auto px-3.75'],
      menuList: ['flex'],
      menuItem: ['grow text-white! font-black'],
      menuItemTitle: [
        'block w-full h-full text-center cursor-pointer',
        'px-2 py-[0.7rem]',
        'uppercase text-[0.938rem] leading-[1.35]',
        'hover:bg-nav-tertiary/65', 'text-white!',
        // Keep the focused item legible instead of falling through to host focus styles
        'focus-visible:bg-nav-tertiary/65',
      ],
      megaMenu: [
        'absolute top-full left-0 z-120',
        'w-full min-w-40 p-[20px]',
        'bg-light-gray text-black',
        'border-y-[5px] border-t-tertiary border-b-secondary',
        'shadow-[0_7px_10px_2px_rgba(0,0,0,0.6)]',
        'header-animate-fade-out',
      ],
      megaMenuContainer: ['flex flex-col'],
      megaMenuGrid: ['grid grid-cols-3 w-full max-w-292.5 mx-auto'],
      l2Title: [
        'uppercase text-[0.938rem]! font-bold! leading-[1.2]! text-nav-primary block',
        'border-b border-b-[#e2e2e2]',
        'pb-2.5 pe-10 mb-2',
      ],
      l3Title: [
        'text-[0.938rem]! font-bold! leading-[1.2]! text-nav-primary block',
        'mt-6 ms-[2.4px] mb-2',
      ],
      menuLink: [
        'inline-block w-full',
        'px-2 py-1',
        'font-medium text-[0.938rem] leading-[1.35] text-black',
        'hover:bg-white hover:text-secondary hover:underline',
      ],
      megaMenuColumn: ['px-3.75'],
      megaMenuColumnGroup: ['mb-6.25 last:mb-0'],
      featuredSection: ['flex items-start justify-center px-3.75 mb-[20px] lg:mb-0'],
      featuredImageOverlay: [
        'relative z-2 w-full h-full p-2',
        'flex flex-col items-start justify-end',
        'border',
      ],
      featuredImageContent: ['w-full p-3.75'],
      featuredContent: [
        'text-white font-black uppercase',
        'text-[1.5rem] leading-7.5 lg:leading-6 mb-3',
      ],
      featuredLink: [
        'px-3 py-1.5 bg-[#f8f9fa] text-nav-primary hover:bg-[#e2e6ea]',
        'rounded-sm font-black text-[0.8rem] leading-normal min-h-8.75 tracking-normal',
        'flex gap-0.5 items-center justify-center w-fit min-w-40',
      ],
      loginSection: ['mb-6.25 flex flex-col'],
      loginContainer: ['w-full max-w-page-template mx-auto px-5 pt-5 bg-white'],
      loginGrid: ['flex pb-3.75'],
      loginFormColumn: ['flex-1 px-3.75 border-e'],
      loginForm: ['py-2 pe-2'],
      loginFormRow: ['flex items-center'],
      loginFormLabel: [
        'text-nav-primary text-[0.938rem] leading-[1.35] font-medium min-[1200px]:text-nowrap',
      ],
      loginUsernameInput: [
        'border border-light-gray rounded-[5px]',
        'text-black text-[0.875rem] font-medium',
        'mx-1.25 px-2.5 py-0.5 h-7.5 flex-1 min-[1200px]:w-[min(295px,30vw)]',
      ],
      loginPasswordWrapper: [
        'mx-1.25 flex flex-1 flex-col relative min-[1200px]:w-[min(295px,30vw)]',
      ],
      loginPasswordInput: [
        'border border-light-gray rounded-[5px]',
        'text-black text-[0.875rem] font-medium',
        'px-2.5 py-0.5 h-7.5 w-full',
      ],
      loginForgotPasswordLink: [
        'absolute -bottom-4',
        'text-[0.75rem]  text-nav-primary leading-[1.35] font-medium underline text-nowrap',
      ],
      loginSubmitButton: [
        'mx-1.25 px-2 py-1 min-h-8.75 w-fit min-w-22.5 text-center shrink-0',
        'rounded-[3.2px]',
        'font-black leading[1.5] text-[0.875rem] uppercase cursor-pointer',
      ],
      logoutButton: [
        'mx-1.25 px-2 py-1 min-h-8.75 w-fit min-w-22.5 text-center',
        'flex items-center justify-center',
        'border border-primary-border bg-secondary rounded-[3.2px]',
        'text-white font-black leading[1.5] text-[0.875rem] uppercase cursor-pointer',
      ],
      loginSignupColumn: ['col-span-3 px-3.75 flex items-center justify-start'],
      loginLoggedInRow: ['flex col-span-full justify-between items-center w-full'],
      loginLoggedInInfo: ['flex grow gap-5 items-center'],
      loginLoggedInIconWrapper: ['w-12.5 h-12.5'],
      loginLoggedInIcon: ['w-full h-full'],
      loginLoggedInActions: ['grow flex justify-end'],
      loginDisclaimerContainer: [
        'px-5 bg-[#f9f9f9] max-w-page-template mx-auto w-full',
        'border-b-2 border-b-secondary',
      ],
      loginDisclaimerWrapper: ['mt-4 px-3.75'],
      identifierLegend: ['w-full max-w-292.5 mx-auto px-3.75 mt-3 flex'],
      identifierLegendItem: [
        'px-2 py-1 flex items-center',
        'font-medium leading-[1.35] text-[1rem] text-textPrimary',
        'gap-1.25 w-fit',
      ],
      identifierIcon: ['h-3 w-auto'],
      topBarActions: ['relative flex gap-5 items-center'],
      searchWrapper: ['hidden lg:flex items-center flex-1 w-[380px]'],
      mobileSearchWrapper: [
        'z-120',
        'w-full md:w-1/2',
        'absolute top-full right-0',
        'lg:hidden',
        'bg-white border-b border-[#dedede] md:border-b-0',
        'p-[15px]',
        'transition-transform origin-top',
      ],
      mobileNav: ['flex lg:hidden divide-x divide-solid'],
      mobileMenuToggle: ['flex flex-col items-center justify-center gap-1 cursor-pointer px-[16px]'],
      toggleIcon: ['h-6 w-6'],
      // Stacks the bars/close glyphs on top of each other so they can cross-fade instead of popping
      // Blockified by the flex parent, so it keeps the same 24px box the single <i> used to occupy
      toggleIconStack: ['relative h-6 w-6'],
      // Shared styling for both stacked glyphs; fa-bars / fa-xmark is chosen in MobileNav
      hamburgerIcon: [
        'absolute inset-0',
        'text-secondary text-lg pt-1',
        'transition-[opacity,transform] duration-300 ease-in-out',
        'motion-reduce:transition-none',
      ],
      // State classes for the stacked glyphs come from the mobileMenuOpen variant below
      menuIconBars: [],
      menuIconClose: [],
      mobileSearchIcon: ['fa-solid fa-magnifying-glass', 'text-lg text-secondary', 'pt-1'],
      menuLabel: ['font-medium text-nav-primary text-[min(3.6vw,1rem)] leading-none'],
    },
    variants: {
      menuVisible: {
        true: {
          menuItemTitle: ['bg-nav-tertiary hover:bg-nav-tertiary!'],
          megaMenu: ['block header-animate-fade-in'],
        },
        false: {
          megaMenu: ['hidden'],
        },
      },
      searchVisible: {
        true: {
          mobileSearchWrapper: ['scale-y-100'],
        },
        false: {
          mobileSearchWrapper: ['scale-y-0'],
        },
      },
      userLoggedIn: {
        true: {
          loginContainer: ['border-b-2 border-b-secondary'],
        },
      },
      // Cross-fades and rotates the hamburger into the close glyph
      mobileMenuOpen: {
        true: {
          menuIconBars: ['opacity-0 rotate-90 scale-50'],
          menuIconClose: ['opacity-100 rotate-0 scale-100'],
        },
        false: {
          menuIconBars: ['opacity-100 rotate-0 scale-100'],
          menuIconClose: ['opacity-0 -rotate-90 scale-50'],
        },
      },
    },
    defaultVariants: {
      mobileMenuOpen: false,
    },
  },
  { twMerge: false }
);

export const mobileHeaderVariants = tv(
  {
    slots: {
      megaMenuContainer: [
        'w-full md:w-1/2 bg-nav-primary',
        'absolute right-0 top-full z-120',
        'transform transition-all duration-300 ease-in-out',
        'px-[16px] pb-[16px]',
      ],
      navList: ['flex flex-col'],
      navItem: ['border-b border-b-white'],
      navItemTitle: [
        'text-white font-bold text-base leading-normal uppercase',
        'py-[20px] w-full flex items-center justify-between cursor-pointer text-start',
        // Pin hover to the resting colors so host CSS can't recolor the item into an unreadable state
        'hover:bg-nav-primary hover:text-white',
      ],
      megaMenu: ['header-animate-fade-out', 'w-full bg-white mt-0.5 pt-3.75 pb-2 px-3.75'],
      topLinksList: ['mb-[20px]'],
      topLink: [
        'flex items-center justify-start gap-2.5 w-full',
        'p-2.5 font-medium leading-normal text-black',
        'hover:text-secondary hover:underline',
      ],
      levelTwoList: ['flex flex-col mb-[20px]'],
      levelTwoTitle: [
        'text-nav-primary font-medium leading-normal uppercase',
        'pt-[8px] pb-2.5 w-full flex items-center justify-between text-start',
        'border-b border-b-[#e2e2e2] cursor-pointer',
      ],
      subMegaMenu: ['header-animate-fade-out', 'w-full bg-white mt-0.5 py-[8px]'],
      levelTwoLink: [
        'flex justify-start items-center gap-2.5',
        'p-2.5 font-medium leading-normal',
        'hover:text-secondary',
      ],
      levelThreeTitle: ['text-nav-primary font-bold leading-[1.2]', 'mb-[8px] inline-block w-full'],
      levelThreeLink: [
        'flex justify-start items-center gap-2.5',
        'p-2.5 font-medium leading-normal',
        'hover:text-secondary',
      ],
      chevronIconL1: ['text-white shrink-0', 'transition-transform duration-250 ease-linear'],
      chevronIconL2: ['text-gray-600 shrink-0', 'transition-transform duration-250 ease-linear'],
      mobileSearchWrapper: ['px-[16px] pt-[16px]'],
      loginWrapper: ['flex flex-col gap-[16px]'],
      loginForm: ['flex flex-col'],
      loginInput: [
        'border border-light-gray rounded-[5px]',
        'text-black text-[16px] font-medium',
        'ps-2.5 py-0.5 pe-0.5 h-7.5 w-full my-1.5',
      ],
      loginSubmitButton: [
        'rounded-[3.2px]',
        'font-black leading-normal text-[0.875rem] uppercase cursor-pointer',
        'px-[8px] py-1 w-fit my-1.5 min-w-22.5',
      ],
      logoutButton: [
        'text-nav-primary text-[1.125rem] font-medium underline',
        'w-fit mb-4',
        'hover:text-secondary',
      ],
      loginFooter: ['flex justify-between items-center border-b-4 border-b-light-gray'],
      loginForgotPasswordLink: ['py-3.75 font-bold text-[0.875rem] hover:underline text-nav-primary'],
    },
    variants: {
      mobileMenuOpen: {
        true: {
          megaMenuContainer: ['translate-y-0 visible'],
        },
        false: {
          megaMenuContainer: ['-translate-y-full invisible'],
        },
      },
      megaMenuVisible: {
        true: {
          megaMenu: ['block header-animate-fade-in'],
          chevronIconL1: ['rotate-180'],
        },
        false: {
          megaMenu: ['hidden'],
        },
      },
      subMegaMenuVisible: {
        true: {
          subMegaMenu: ['block header-animate-fade-in'],
          chevronIconL2: ['rotate-180'],
        },
        false: {
          subMegaMenu: ['hidden'],
        },
      },
    },
  },
  { twMerge: false }
);
